/**
 * ImageForm: edit form for the `image` block.
 *
 * data: { url, alt, caption? }
 *
 * Plain inputs. `alt` is surfaced as recommended-for-accessibility text but is
 * not hard-required here (the renderer falls back to an empty alt).
 *
 * Round-3 review finding 4: surfaces an inline warning when the URL fails
 * the image allowlist, so the author sees actionable feedback rather than
 * a generic "Failed to save the draft." after the server's validateBlockUrls
 * returns 400. The warning is advisory: the save button is NOT disabled,
 * because the server-side validator is the security boundary and the user
 * may be mid-paste / mid-fix.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-bootstrap';

import { isAllowedImageUrl } from '../../utils/urlValidator';

export default function ImageForm({ data, onChange }) {
    const d = data || {};

    const update = (field, value) => {
        onChange({ ...d, [field]: value });
    };

    const urlInvalid = Boolean(d.url) && !isAllowedImageUrl(d.url);

    return (
        <>
            <Form.Group className="mb-3" controlId="image-url">
                <Form.Label>Image URL</Form.Label>
                <Form.Control
                    type="url"
                    value={d.url || ''}
                    onChange={(e) => update('url', e.target.value)}
                    placeholder="https://…"
                    isInvalid={urlInvalid}
                />
                {urlInvalid && (
                    <Form.Control.Feedback type="invalid">
                        Image URL must be http(s) or a same-site path (e.g.
                        /uploads/x.png). data:, javascript:, and
                        protocol-relative URLs are rejected on save.
                    </Form.Control.Feedback>
                )}
            </Form.Group>
            <Form.Group className="mb-3" controlId="image-alt">
                <Form.Label>Alt Text</Form.Label>
                <Form.Control
                    type="text"
                    value={d.alt || ''}
                    onChange={(e) => update('alt', e.target.value)}
                    placeholder="Describe the image for screen readers"
                />
                <Form.Text muted>
                    Describe the image so it is accessible to screen-reader
                    users.
                </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3" controlId="image-caption">
                <Form.Label>Caption</Form.Label>
                <Form.Control
                    type="text"
                    value={d.caption || ''}
                    onChange={(e) => update('caption', e.target.value)}
                    placeholder="Optional caption shown below the image"
                />
            </Form.Group>
        </>
    );
}

ImageForm.propTypes = {
    data: PropTypes.object,
    onChange: PropTypes.func.isRequired,
};

ImageForm.defaultProps = {
    data: {},
};
